import Icon from 'molecules/Icon';

import type { Api } from '@zag-js/carousel';
import type { ComponentPropsWithoutRef, FC } from 'react';

interface PaginationProps {
  /**
   * The `api` prop is an object that provides access to the carousel API, which can be used to control the carousel's behavior and state.
   */
  api: Api;
  /**
   * The `slides` prop is an array of slide objects that represent the content to be displayed in the carousel.
   * Each slide object should have the necessary properties to render the content for that slide.
   */
  slides: unknown[];
}

const SliderPaginationButton: FC<ComponentPropsWithoutRef<'button'>> = ({ children, ...props }) => (
  <button
    className="flex min-h-11 min-w-11 items-center justify-center rounded-full bg-gray-900/90 stroke-[1.5] text-primary-300 shadow-xl transition-colors focus-outline-primary hover:bg-black hover:text-primary-400 dark:bg-white/80 dark:text-primary-700 dark:hover:bg-white dark:hover:text-primary-800"
    {...props}
  >
    {children}
  </button>
);

const Pagination: FC<PaginationProps> = ({ api, slides }) => (
  <div className="m-auto flex w-fit items-center gap-6">
    <SliderPaginationButton {...api.getPrevTriggerProps()}>
      <Icon icon="arrow-left" size={24} />
    </SliderPaginationButton>
    <div className="flex w-full gap-3" {...api.getIndicatorGroupProps()}>
      {slides.map((_, index) => (
        <button
          // eslint-disable-next-line react/no-array-index-key
          key={index}
          className="size-3 rounded-full bg-gray-900/70 focus-outline-primary data-[current]:bg-primary-800 dark:bg-white/40 dark:data-[current]:bg-primary-100"
          {...api.getIndicatorProps({ index })}
          aria-label={`Slide indicator ${index}`}
        />
      ))}
    </div>
    <SliderPaginationButton {...api.getNextTriggerProps()}>
      <Icon icon="arrow-right" size={24} />
    </SliderPaginationButton>
  </div>
);

export default Pagination;
